import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'
import { OtpDigitInput } from '../atoms/OtpDigitInput'

/**
 * VerificationOtpStep — the screen the user sees when the pipeline runtime
 * pauses at `verification_check`. Renders a heading, the OTP cell row, an
 * inline error slot, the Verify submit button, and a Resend control that
 * counts down a cooldown before re-enabling.
 *
 * Composition: heading copy + sub-heading + OtpDigitInput (atom) + Button
 * (atom). No network calls — `onSubmit` and `onResend` come from the parent
 * so the same molecule works for email_otp, sms, and any future channel.
 *
 * A11y:
 *   - The OTP cells live in a labelled group ("verification code") with
 *     per-cell labels ("Digit 1 of 6, verification code").
 *   - Inline error is `role="alert"` + `aria-live="polite"` so AT users hear
 *     the failure when the parent sets `error`.
 *   - When `error` is non-empty every cell is `aria-invalid="true"`.
 *
 * Auto-submit:
 *   Off by default. Turn on via `autoSubmitOnComplete` only when the
 *   surrounding flow makes a hidden-action OK (a11y guideline: surprise
 *   submissions hurt screen-reader users). The Verify button always works
 *   as a manual fallback.
 */

export interface VerificationOtpStepProps {
  /** Channel — drives the copy ("code via SMS" vs "code"). */
  channel: 'email' | 'sms'
  /** Masked recipient shown in the heading (e.g. "a***@gmail.com"). */
  maskedAddress: string
  /** Number of cells. Default 6. */
  codeLength?: number
  /** Seconds before Resend re-enables. Default 60. */
  resendCooldownSeconds?: number
  /** Called when the user submits the code (Verify button or Enter). */
  onSubmit: (code: string) => void | Promise<void>
  /** Called when the user clicks Resend. */
  onResend: () => Promise<void>
  /** Inline error from the parent. */
  error?: string
  /** Set true while submit or resend is in flight. */
  busy?: boolean
  /** Optional helper text rendered under the heading. */
  helperText?: string
  /** Auto-submit when the last cell is filled. Default false. */
  autoSubmitOnComplete?: boolean
  /**
   * Called when the user clicks the "Skip for now" tertiary button. Renders
   * only when the parent passes onSkip. Used by the credential_set
   * channel-OTP enrollment pause when the admin configured `optional: true`
   * (env.can_skip = true). For the verification_check pause this is always
   * undefined — you cannot skip email/phone verification.
   */
  onSkip?: () => void | Promise<void>
  /** Override the skip button label. Default: "Skip for now". */
  skipLabel?: string
  /** Extra classes for the wrapper. */
  className?: string
}

export const VerificationOtpStep = React.forwardRef<HTMLDivElement, VerificationOtpStepProps>(
  (
    {
      channel,
      maskedAddress,
      codeLength = 6,
      resendCooldownSeconds = 60,
      onSubmit,
      onResend,
      error,
      busy = false,
      helperText,
      autoSubmitOnComplete = false,
      onSkip,
      skipLabel = 'Skip for now',
      className,
    },
    ref,
  ) => {
    const [code, setCode] = React.useState('')
    const [secondsLeft, setSecondsLeft] = React.useState(resendCooldownSeconds)
    const [resending, setResending] = React.useState(false)
    const submittedAutoForRef = React.useRef<string | null>(null)

    // Pause-transition reset: when the parent flow advances from one
    // verification step to a different one (typical case: email OTP
    // verified -> SMS OTP pause), the component instance is reused
    // and React preserves the digit state across the prop change.
    // Without this effect the user sees the prior step's code
    // prefilled into the next channel's cells -- which they then
    // submit by accident, triggering an "invalid code" failure that
    // burns the freshly-minted token (single-strike-out semantics).
    //
    // Keying on (channel, maskedAddress) covers BOTH:
    //   - channel change (email -> sms)
    //   - same-channel different recipient (resend that re-masks)
    //
    // Also resets the cooldown timer so the new step gets its own
    // 60s window before Resend re-enables. submittedAutoForRef
    // resets so auto-submit can fire once for the next code.
    // biome-ignore lint/correctness/useExhaustiveDependencies: setCode/setSecondsLeft are stable React state setters and submittedAutoForRef is a ref; only re-fire on pause-identity change
    React.useEffect(() => {
      setCode('')
      setSecondsLeft(resendCooldownSeconds)
      submittedAutoForRef.current = null
    }, [channel, maskedAddress, resendCooldownSeconds])

    // Countdown — ticks once a second while > 0. Cleans up on unmount + on
    // any resend (which resets the timer back to the configured cooldown).
    React.useEffect(() => {
      if (secondsLeft <= 0) return
      const id = window.setInterval(() => {
        setSecondsLeft((s) => (s <= 1 ? 0 : s - 1))
      }, 1000)
      return () => window.clearInterval(id)
    }, [secondsLeft])

    const handleSubmit = React.useCallback(async () => {
      if (busy) return
      if (code.length !== codeLength) return
      await onSubmit(code)
    }, [busy, code, codeLength, onSubmit])

    // Auto-submit once the cells are full — guarded by a ref so a stable
    // value doesn't re-fire if the parent re-renders without clearing.
    React.useEffect(() => {
      if (!autoSubmitOnComplete) return
      if (code.length !== codeLength) {
        submittedAutoForRef.current = null
        return
      }
      if (submittedAutoForRef.current === code) return
      submittedAutoForRef.current = code
      void handleSubmit()
    }, [autoSubmitOnComplete, code, codeLength, handleSubmit])

    const handleResend = React.useCallback(async () => {
      if (resending || busy || secondsLeft > 0) return
      setResending(true)
      try {
        await onResend()
        // Reset countdown + clear the input so the user types the fresh code.
        setSecondsLeft(resendCooldownSeconds)
        setCode('')
        submittedAutoForRef.current = null
      } finally {
        setResending(false)
      }
    }, [busy, onResend, resendCooldownSeconds, resending, secondsLeft])

    const heading =
      channel === 'sms'
        ? `Enter the ${codeLength}-digit code we sent via SMS to ${maskedAddress}`
        : `Enter the ${codeLength}-digit code we sent to ${maskedAddress}`

    const resendLabel = resending
      ? 'Sending…'
      : secondsLeft > 0
        ? `Resend in ${secondsLeft}s`
        : 'Resend code'

    const submitDisabled = busy || code.length !== codeLength

    return (
      <div ref={ref} className={cn('flex flex-col gap-4', className)}>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="text-heading-sm font-semibold text-ia-text">{heading}</h2>
          {helperText ? <p className="text-body-sm text-ia-text-secondary">{helperText}</p> : null}
        </div>

        <OtpDigitInput
          value={code}
          onChange={setCode}
          onEnter={handleSubmit}
          length={codeLength}
          autoFocus
          disabled={busy}
          invalid={!!error}
          aria-label="verification code"
        />

        {/*
          Reserve a row for the error so layout doesn't jump in/out. The
          live region stays mounted (empty when no error) which keeps SR
          announcements deterministic.
        */}
        <div
          role="alert"
          aria-live="polite"
          className={cn(
            'min-h-5 text-center text-body-sm',
            error ? 'text-ia-error' : 'text-transparent',
          )}
        >
          {error || 'placeholder'}
        </div>

        <Button
          type="button"
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={submitDisabled}
          loading={busy}
          className="w-full"
        >
          {busy ? 'Verifying…' : 'Verify'}
        </Button>

        <div className="flex items-center justify-center gap-1 text-body-sm text-ia-text-secondary">
          <span>Didn’t get it?</span>
          <Button
            type="button"
            variant="link"
            size="sm"
            onClick={handleResend}
            disabled={busy || resending || secondsLeft > 0}
            // Link-variant Button doesn't render its own loading spinner; we
            // surface "Sending…" via the label so the visual stays calm.
          >
            {resendLabel}
          </Button>
        </div>

        {onSkip ? (
          // Tertiary skip — mirrors TotpEnrollStep + WebAuthnEnrollStep +
          // FactorPickerStep. Only rendered for channel-OTP enrollment (the
          // verify-an-email-you-own case never carries can_skip).
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void onSkip()
            }}
            className={cn(
              'mx-auto rounded-md px-3 py-1.5 text-body-sm font-medium text-ia-text-secondary',
              'transition-colors duration-150 hover:text-ia-text',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {skipLabel}
          </button>
        ) : null}
      </div>
    )
  },
)
VerificationOtpStep.displayName = 'VerificationOtpStep'
